/**
 * Consultas e mutações de clientes (`/clients`) e a lista de estados (`/states`),
 * com TanStack Query.
 */import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, patch, post } from '@/lib/api';
import type {
  Client,
  ClientsQuery,
  CreateClientInput,
  ProposalStatus,
  StateTax,
  UpdateClientInput,
} from '@/lib/types';

// -------------------------------------------------------------------- tipos --

/** Linha de proposta recente, no formato reduzido que `GET /clients/:id` de fato devolve. */
export interface PropostaRecenteCliente {
  id: number;
  code: string;
  createdAt: string;
  totalGeral: number;
  status: ProposalStatus;
}

export interface ClienteDetalhe extends Client {
  proposalsCount: number;
  recentProposals: PropostaRecenteCliente[];
}

/** Linha de `GET /clients`: também traz `proposalsCount`, que `Client` (tipo genérico) não declara. */
export interface ClienteResumo extends Client {
  proposalsCount: number;
}

// ------------------------------------------------------------------ helpers --

/**
 * Aceita a resposta de um endpoint de cliente único em qualquer uma das duas
 * formas — `T` cru ou `{ data: T }` — e sempre devolve `T`. Ver nota no topo
 * do arquivo: cobre a transição do backend para o envelope padrão.
 */
function queryString(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') usp.set(key, String(value));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

const clientesKeys = {
  lista: (filtro: ClientsQuery) => ['clientes', 'lista', filtro] as const,
  detalhe: (id: number | undefined) => ['clientes', 'detalhe', id] as const,
  todos: ['clientes'] as const,
};

// ------------------------------------------------------------------ queries --

export function useClientesLista(filtro: ClientsQuery = {}) {
  return useQuery({
    queryKey: clientesKeys.lista(filtro),
    queryFn: () =>
      get<{ data: ClienteResumo[]; page: number; perPage: number; total: number }>(
        `/clients${queryString({
          q: filtro.q,
          uf: filtro.uf,
          active: filtro.active,
          page: filtro.page,
          perPage: filtro.perPage,
        })}`
      ),
    placeholderData: keepPreviousData,
  });
}

export function useCliente(id: number | undefined) {
  return useQuery({
    queryKey: clientesKeys.detalhe(id),
    queryFn: () => get<{ data: ClienteDetalhe }>(`/clients/${id}`).then((r) => r.data),
    enabled: id !== undefined,
  });
}

/** Estados/alíquotas — usado no seletor de UF. Muda raramente: `staleTime` maior. */
export function useEstados() {
  return useQuery({
    queryKey: ['estados'],
    queryFn: () => get<{ data: StateTax[] }>('/states').then((r) => r.data),
    staleTime: 5 * 60_000,
  });
}

// ---------------------------------------------------------------- mutações --

export function useCriarCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateClientInput) =>
      post<{ data: Client }>('/clients', input).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: clientesKeys.todos }),
  });
}

export function useAtualizarCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateClientInput }) =>
      patch<{ data: Client }>(`/clients/${id}`, input).then((r) => r.data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: clientesKeys.detalhe(id) });
      qc.invalidateQueries({ queryKey: ['clientes', 'lista'] });
    },
  });
}

export function useExcluirCliente() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => del<null>(`/clients/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: clientesKeys.todos }),
  });
}
